/**
 * The permissions Cardinal issues for this application.
 *
 * **The numbers are not a hierarchy.** They come from the previous structure, which had modules, sub
 * modules and components; the middle level is gone and its numbers were reused, so a value in the
 * 5000 range says nothing about which module it belongs to. `SENTINEL_CAN_USE_LOG_ENTRIES` is 5101
 * and belongs to Monitoring, whose module permission is 5500. **Read the name, never the range.**
 */
export enum Permission {
  COMMON_IGNORE_SUBSCRIPTION_ASSIGNMENT = 100,
  /**
   * **Not a gate for this application, despite the name.** It governs core routes in Accounts that
   * carry no permission of their own; nothing in Sentinel depends on it, and nothing should start
   * to. What someone may do here is decided by the module and screen permissions alone.
   */
  COMMON_IS_SENTINEL_USER = 102,
  SENTINEL_CAN_USE_MODULE_TENANTS = 3000,
  SENTINEL_CAN_USE_TENANTS = 3101,
  SENTINEL_CAN_CREATE_TENANT = 3102,
  SENTINEL_CAN_DELETE_TENANT = 3103,
  SENTINEL_CAN_USE_TENANT_DETAILS = 3105,
  SENTINEL_CAN_USE_TENANT_ASSIGNED_ACCOUNTS = 3106,
  SENTINEL_CAN_EDIT_TENANT = 3107,
  SENTINEL_CAN_USE_MODULE_USERS = 4000,
  SENTINEL_CAN_USE_USER_ACCOUNTS = 4101,
  SENTINEL_CAN_USE_USER_ACCOUNT_DETAILS = 4103,
  SENTINEL_CAN_EDIT_USER_ACCOUNT_DETAILS = 4104,
  SENTINEL_CAN_USE_USER_ACCOUNT_SUBSCRIPTIONS = 4105,
  SENTINEL_CAN_WRITE_USER_ACCOUNT_SUBSCRIPTIONS = 4106,
  SENTINEL_CAN_USE_MODULE_INFRASTRUCTURE = 5000,
  SENTINEL_CAN_USE_LOG_ENTRIES = 5101,
  SENTINEL_CAN_USE_SCHEDULER_EVENTS = 5201,
  SENTINEL_CAN_EDIT_SCHEDULER_EVENTS = 5202,
  SENTINEL_CAN_USE_SCHEDULER_EVENT_TYPES = 5203,
  SENTINEL_CAN_EDIT_SCHEDULER_EVENT_TYPES = 5204,
  SENTINEL_CAN_USE_MODULE_MONITORING = 5500
}

/**
 * Unpacks the permission string carried in the user's app metadata.
 *
 * **One character per permission, and the character code is the number.** That is the whole format --
 * `PermissionExtensions` on the server side writes it with `(char)permission` and reads it back with
 * `(int)character`. Nothing is delimited, so nothing has to be parsed.
 *
 * `codePointAt` rather than `charCodeAt`, and the spread rather than `split('')`: both matter only if
 * a value ever landed above 0xFFFF, which none of these do -- but the pair that is correct for every
 * value costs nothing over the pair that is correct for these.
 *
 * An empty or missing string is no permissions, which is not the same as an error: a user who has
 * been given none is a user who may do nothing here.
 */
export function permissionsOf(packed: string | null | undefined): ReadonlySet<number> {
  if (packed === null || packed === undefined || packed === '') {
    return new Set();
  }

  return new Set([...packed].map(character => character.codePointAt(0) ?? 0));
}
